import { DocumentItemPartial, DocumentItemTypeEnum, DocumentSessionPartial } from '@domain';
import { RaImageFullScreenModel } from 'ra-ui';

/**
 * Galeria do documento: todas as fotos numa lista única + o deslocamento inicial
 * de cada item que contém foto.
 *
 * `offsets` é indexado por `"<índice da seção>:<índice do item>"` — os mesmos
 * `$index` dos `@for` do template do preview — para não depender de id de item
 * (item recém-criado no editor pode não ter `_id` ainda).
 */
export interface PreviewGallery {
    images: RaImageFullScreenModel[];
    offsets: Map<string, number>;
}

/** Chave usada em `offsets`. */
export function previewGalleryKey(sectionIndex: number, itemIndex: number): string {
    return `${sectionIndex}:${itemIndex}`;
}

/** URLs de foto de um item; vazio para itens que não são imagem. */
function itemImageUrls(item: DocumentItemPartial): string[] {
    switch (item?.type) {
        case DocumentItemTypeEnum.IMAGE:
            return item.value ? [item.value as string] : [];
        case DocumentItemTypeEnum.GROUP_IMAGE:
            return ((item.value as { url: string }[]) ?? [])
                .filter(image => !!image?.url)
                .map(image => image.url);
        default:
            return [];
    }
}

/**
 * Achata as fotos do documento na ordem em que aparecem (seção → item → foto do
 * grupo). A tela cheia do preview é do DOCUMENTO, não do card: clicar numa foto
 * abre nela, mas dá para percorrer o documento inteiro sem fechar e reabrir card
 * a card.
 *
 * `fallbackLabel` é o alt/title usado quando o item não tem nome.
 */
export function buildPreviewGallery(
    sessions: DocumentSessionPartial[] | undefined | null,
    fallbackLabel: string
): PreviewGallery {
    const images: RaImageFullScreenModel[] = [];
    const offsets = new Map<string, number>();

    (sessions ?? []).forEach((section, sectionIndex) => {
        (section?.items ?? []).forEach((item, itemIndex) => {
            const urls = itemImageUrls(item);
            if (!urls.length) return;

            offsets.set(previewGalleryKey(sectionIndex, itemIndex), images.length);

            const label = item?.name || fallbackLabel;
            urls.forEach(url => images.push({ url, alt: label, title: label }));
        });
    });

    return { images, offsets };
}
